import { call, put, takeLatest, select } from "redux-saga/effects";
import { routerActions, replace, push } from "connected-react-router";
import { downloadXls, downloadEcheqTemplateCsvAsExcel } from "util/download";
import { types, selectors as echeckSelectors, actions as echeckActions } from "reducers/echecks";
import { actions as notificationActions } from "reducers/notification";
import { selectors as sessionSelectors } from "reducers/session";
import { selectors as accountSelectors } from "reducers/accounts";
import { cleanEcheqObject } from "pages/echeck/utils";
import { store } from "store";
import moment from "moment";
import * as echecksMiddleware from "middleware/echecks";
import * as configUtils from "util/config";
import { transactionStatus, getStateByCode } from "pages/transactions/constants";
import * as i18n from "util/i18n";

import * as form from "middleware/form";
import { credentialsErrorMessage } from "util/validateMessage";

// import { INSUFFICIENT_SIGNATURES } from "util/responses";

const sagas = [
    takeLatest(types.FILTER_ECHEQ_LIST, filterEcheqList),
    takeLatest(types.DOWNLOAD_ECHEQ_LIST_REQUEST, downloadEcheqList),
    takeLatest(types.EMIT_ECHEQ, emitEcheq),
    takeLatest(types.EXECUTE_ACTION_ECHEQ, executeEcheqActionSagas),
    takeLatest(types.EXECUTE_ACTION_ECHEQ_TRANSACTION, executeEcheqTransactionActionSagas),
    takeLatest(types.EXECUTE_ACTION_ECHEQ_MULTIPLE, executeEcheqMultipleActionSagas),
    takeLatest(types.ENDORSE_ECHEQ, endorseEcheq),
    takeLatest(types.ENDORSE_MULTIPLE_ECHEQ, endorseMutlipleEcheq),
    takeLatest(types.CESION_ECHEQ, cesionEcheq),
    takeLatest(types.ECHEQ_DETAIL_REQUEST, echeqDetailRequest),
    takeLatest(types.AMOUNT_ECHECK_REQUEST, amountEchecksRequest),
    takeLatest(types.ACCEPT_RETURN_ECHEQ, acceptReturnEcheq),
    takeLatest(types.SET_REQUEST_RETURN_SOLICITANT_NAME, getRequestReturnSolicitant),
    takeLatest(types.BANKED_BENEFICIARY, getBankedBenefiicary),
    takeLatest(types.REQUEST_AMOUNT_ECHECK_DATA, updateEcheckAmount),
    takeLatest(types.SOLICIT_AVAL_REQUEST, solicitAvalRequest),
    takeLatest(types.UPDATE_AVAL_REQUEST, updateStateAvalRequest),
    takeLatest(types.DOWNLOAD_MASSIVE_ECHEQ_TEMPLATE_REQUEST, downloadMassiveTemplate),
    takeLatest(types.CREATE_MASSIVEECHEQ_REQUEST, validateMassiveEcheq),
    takeLatest(types.CANCEL_TRANSFER_REQUEST, cancelTransfer),
    takeLatest(types.CANCEL_ENDORSE_ECHEQ, cancelEndorseEcheq),
    takeLatest(types.REQUEST_CAC_REQUEST, requestCac),
    takeLatest(types.ACCEPT_RETURN_REJECTED_REQUEST, acceptReturnRejected),
];

export default sagas;

const ticketStates = {
    COR020W: "ERROR",
    COR023I: transactionStatus.PENDING,
    COR000I: transactionStatus.FINISHED,
    COR013I: transactionStatus.SCHEDULED,
    COR060I: transactionStatus.PROCESSING,
    API515I: transactionStatus.PENDING,
    API516W: transactionStatus.FAILED,
    BAK631W: transactionStatus.FAILED,
};

const repudiateActions = ["REPUDIATE", "REPUDIATE-CESION", "REPUDIATE-RETURN"];

/**
 * Calcula el monto total de una lista de echeqs
 * @param {Array} echeqList - Array de echeqs con propiedad 'monto' y 'moneda'
 * @returns {Object} - Objeto Amount con currency y quantity
 */
export const calculateTotalAmount = (echeqList) => {
    if (!echeqList || !Array.isArray(echeqList) || echeqList.length === 0) {
        return {
            currency: null,
            quantity: 0,
        };
    }

    const currency = echeqList[0]?.moneda || null;
    const quantity = echeqList.reduce((total, echeq) => {
        const monto = Number(echeq?.monto) || 0;
        return total + monto;
    }, 0);

    return {
        currency,
        quantity,
    };
};

function* getRequestReturnSolicitant({ cuit }) {
    if (cuit) {
        const response = yield call(echecksMiddleware.bankedBeneficiary, { type: "CUIT", document: cuit });
        yield put({
            type: types.SET_REQUEST_RETURN_SOLICITANT_NAME,
            solicitantName: response.data.data.beneficiary.beneficiario_razon_social,
        });
    }
}

function* filterEcheqList({ filters }) {
    try {
        const option = echeckSelectors.getEcheqOption(store.getState());
        const payedCheck = !!(filters.payedFrom && filters.payedTo);
        const emitedCheck = !!(filters.createdFrom && filters.createdTo);
        const reportInfo = true; // full echeq data
        const params = { ...filters, option, payedCheck, emitedCheck, reportInfo };
        const response = yield call(echecksMiddleware.filterEcheqsList, params);
        if (response.type === "W") {
            yield put(notificationActions.showNotification(response.data.message, "error", ["echecks"]));
        } else if (response.data.data.codigo !== "2400") {
            yield put(notificationActions.showNotification(response.data.data.description, "error", ["echecks"]));
        } else {
            yield put({
                type: types.SET_ECHEQ_LIST,
                echeqList: response.data.data.echeqs,
                totalPages: response.data.data.totalPages,
                pageNumber: response.data.data.pageNumber,
                environmentCuit: response.data.data.environmentCuit,
            });
        }
    } finally {
        yield put({
            type: types.SET_ECHEQ_FETCHING,
        });
    }
}

function* downloadEcheqList({ filters }) {
    const payedCheck = !!(filters.payedFrom && filters.payedTo);
    const emitedCheck = !!(filters.createdFrom && filters.createdTo);
    const params = { ...filters, payedCheck, emitedCheck };
    const response = yield call(echecksMiddleware.downloadEcheqsList, params);
    if (response.type === "W") {
        yield put(notificationActions.showNotification(response.data.message, "error", ["echeqs"]));
    } else {
        yield put(notificationActions.showNotification(i18n.get("global.successDownload"), "success", ["echeqs"]));

        const { fileName, content } = response.data.data;

        downloadXls(fileName, content);

        yield put({
            type: types.DOWNLOAD_ECHEQ_LIST_SUCCESS,
        });
    }
}

function* downloadMassiveTemplate() {
    const response = yield call(echecksMiddleware.downloadMassiveTemplate, {
        idTemplate: "TEMPLATE_ECHEQ_MASSIVE",
        nameFile: "plantilla_ejemplo",
        format: "txt",
    });
    if (response.type === "W") {
        yield put(notificationActions.showNotification(response.data.message, "error", ["echeqs"]));
    } else {
        yield put(notificationActions.showNotification(i18n.get("global.successDownload"), "success", ["echeqs"]));

        const { fileName, content } = response.data.data;

        try {
            downloadEcheqTemplateCsvAsExcel(fileName, content);
        } catch (error) {
            console.log(error);
            // error al descargar archivo
        }
        yield put({
            type: types.DOWNLOAD_ECHEQ_LIST_SUCCESS,
        });
    }
}

function* emitEcheq({ echeqEmitData, formikBag, credentialType }) {
    const { userId } = sessionSelectors.getUser(store.getState());
    const { uniformBankCode } = accountSelectors.getSelectedAccount(store.getState());
    const { beneficiary = "" } = echeqEmitData;
    const environmentCuit = echeckSelectors.getEnvironmentCuit(store.getState());
    const echeqsToEmit = echeckSelectors.getEcheqsToEmit(store.getState());

    try {
        let response;
        let params;

        if (echeqEmitData.idTransaction) {
            params = { ...echeqEmitData, userId };
            response = yield call(echecksMiddleware.signEmitEcheq, params);
        } else {
            params = {
                userId,
                environmentCuit,
                ...echeqEmitData,
                emision: uniformBankCode,
                echeqsToEmit,
                beneficiary: beneficiary.document,
                beneficiaryName: beneficiary.reference,
            };

            const responseEmitPre = yield call(echecksMiddleware.sendEmitEcheq, params);

            if (responseEmitPre.type === "W") {
                yield put(
                    notificationActions.showNotification(
                        responseEmitPre.data.data?.signatureError ||
                            responseEmitPre.data.data?.NO_FIELD ||
                            responseEmitPre.data.message ||
                            i18n.get("global.unexpectedError"),
                        "error",
                        ["echecks/emit/confirm", "emitEcheck.transaction.page"],
                    ),
                );
                return;
            }

            // Pass the complete response data from sendEmitEcheq to signEmitEcheq
            response = yield call(echecksMiddleware.signEmitEcheq, responseEmitPre.data.data);
        }

        if (response.type === "W") {
            if (response.data?.data?.password || response.data?.data?.otp) {
                const msj =
                    credentialType === "password"
                        ? "preferences.password.password.incorrect"
                        : "login.step1.secondFactor.invalid";
                yield put(
                    notificationActions.showNotification(i18n.get(msj), "error", [
                        "echecks/emit/confirm",
                        "emitEcheck.transaction.page",
                    ]),
                );
                formikBag.setErrors({
                    credential: i18n.get(msj),
                });
            } else {
                yield put(
                    notificationActions.showNotification(
                        response.data.data?.signatureError ||
                            response.data.data?.NO_FIELD ||
                            response.data.message ||
                            i18n.get("global.unexpectedError"),
                        "error",
                        ["echecks/emit/confirm", "emitEcheck.transaction.page"],
                    ),
                );
            }

            if (response?.data?.code === "API516W") {
                yield put({
                    type: types.ECHECK_TICKET_SUCCESS,
                    echeckTicket: {
                        status: ticketStates[response.data.code],
                        ...response.data,
                    },
                });

                yield put(push(`/echecks/emit/failed/${response.data.idTransaction}`));
            }
        }

        if (response.type === "I") {
            yield put({
                type: types.ECHECK_TICKET_SUCCESS,
                echeckTicket: {
                    status: ticketStates[response.data.code],
                    ...response.data,
                },
            });
        }

        if (response.type === "E") {
            yield put({
                type: types.ECHECK_TICKET_SUCCESS,
                echeckTicket: {
                    status: "FAILED",
                    ...response.data,
                },
            });
            yield put(replace(`/echecks/emit/failed/${response.data.idTransaction}`));
        }
    } finally {
        formikBag.setSubmitting(false);
    }
}

function* endorseEcheq({ echeqEndorseData, formikBag, credentialType }) {
    const { userId } = sessionSelectors.getUser(store.getState());
    const { endorseData, echeqAction } = echeqEndorseData;
    const { endorseData: endorseDataObject, beneficiary } = endorseData;
    const echeqActionCleaned = cleanEcheqObject(echeqAction);
    const amount = calculateTotalAmount(echeqAction.echeqList);
    const params = {
        [`_${credentialType}`]: echeqEndorseData[`_${credentialType}`],
        userId,
        ...endorseDataObject,
        ...echeqActionCleaned,
        beneficiary: endorseDataObject.idTransaction ? endorseDataObject.beneficiary.document : beneficiary.document,
        beneficiaryName: endorseDataObject.idTransaction
            ? endorseDataObject.beneficiary.reference
            : beneficiary.reference,
        amount,
    };

    try {
        const response = yield call(echecksMiddleware.sendEndorseEcheq, params);
        const { description, error } = response.data.data;

        if (response.type === "W" && response.data.code !== "API516W") {
            if (response.data.code === "COR020W") {
                if (response.data.data.otp) {
                    formikBag.setErrors({
                        credential: i18n.get("validate.secondFactor.invalid"),
                    });
                    formikBag.setSubmitting(false);
                    return;
                }
            } else {
                yield put(
                    notificationActions.showNotification(response.data.message, "error", ["echecks/endorse/confirm"]),
                );
            }
        } else {
            if (response.data.code === "API516W") {
                yield put(replace(`/echecks/action/endorse/failed/${response.data.idTransaction}`));
                yield put(
                    notificationActions.showNotification(response.data?.data?.description, "error", [
                        "echecks/endorse/confirm",
                    ]),
                );
                return;
            }
            // yield put(formActions.readTransaction({ pathname: `/transaction/${response.data.idTransaction}` }));
            // yield take("READ_TRANSACTION_REQUEST"); // waiting until finish action
            yield put({
                type: types.CLEAN_ECHEQ_ACTION,
            });
            yield put({
                type: types.ECHECK_TICKET_SUCCESS,
                echeckTicket: {
                    status: ticketStates[response.data.code],
                    ...response.data,
                },
            });

            if (ticketStates[response.data.code] === ticketStates.COR000I) {
                yield put(notificationActions.showNotification(description, error, ["echecks/endorse"]));
            }
        }
    } finally {
        formikBag.setSubmitting(false);
    }
}

function* cesionEcheq({ echeqCesionData, formikBag, credentialType }) {
    const { userId } = sessionSelectors.getUser(store.getState());
    const { cesionData, echeqAction } = echeqCesionData;
    const { cesionData: cesionDataObject, beneficiary } = cesionData;
    const echeqActionCleaned = cleanEcheqObject(echeqAction);
    const amount = calculateTotalAmount(echeqAction.echeqList);
    const params = {
        [`_${credentialType}`]: echeqCesionData[`_${credentialType}`],
        userId,
        ...cesionDataObject,
        ...echeqActionCleaned,
        beneficiary: cesionDataObject.idTransaction ? cesionDataObject.beneficiary.document : beneficiary.document,
        beneficiaryName: cesionDataObject.idTransaction
            ? cesionDataObject.beneficiary.reference
            : beneficiary.reference,
        amount,
    };

    try {
        let response;
        if (echeqCesionData.idTransaction) {
            response = yield call(echecksMiddleware.sendCesionEcheq, {
                ...params,
                idTransaction: echeqCesionData.idTransaction,
            });
        } else {
            response = yield call(echecksMiddleware.sendCesionEcheq, params);
        }
        const { description, error } = response.data.data;

        if (response.type === "W" && response.data.code !== "API516W") {
            if (response.data.code === "COR020W") {
                yield put(
                    notificationActions.showNotification(response.data?.message, "error", ["echecks/cesion/confirm"]),
                );
                if (response.data?.data?.password) {
                    const msj =
                        credentialType === "password"
                            ? "preferences.password.password.incorrect"
                            : "login.step1.secondFactor.invalid";
                    yield put(notificationActions.showNotification(i18n.get(msj), "error", ["echecks/cesion/confirm"]));
                    formikBag.setErrors({
                        credential: i18n.get(msj),
                    });
                }
            } else {
                yield put(
                    notificationActions.showNotification(response.data.message, "error", ["echecks/cesion/confirm"]),
                );
            }
        } else {
            if (response.data.code === "API516W") {
                yield put(
                    notificationActions.showNotification(response.data?.data?.description, "error", [
                        "echecks/cesion/confirm",
                    ]),
                );
                return;
            }
            // yield put(formActions.readTransaction({ pathname: `/transaction/${response.data.idTransaction}` }));
            // yield take("READ_TRANSACTION_REQUEST"); // waiting until finish action
            yield put({
                type: types.CLEAN_ECHEQ_ACTION,
            });
            yield put({
                type: types.ECHECK_TICKET_SUCCESS,
                echeckTicket: {
                    status: ticketStates[response.data.code],
                    ...response.data,
                },
            });
            if (ticketStates[response.data.code] === ticketStates.COR000I) {
                yield put(notificationActions.showNotification(description, error, ["echecks/cesion"]));
            }
        }
    } finally {
        formikBag.setSubmitting(false);
    }
}

function* endorseMutlipleEcheq({ echeqEndorseData, formikBag, credentialType }) {
    const { userId } = sessionSelectors.getUser(store.getState());
    const { endorseData, echeqAction } = echeqEndorseData;
    const { endorseData: endorseDataObject, beneficiary = "" } = endorseData;
    const echeqActionCleaned = cleanEcheqObject(echeqAction);
    const params = {
        [`_${credentialType}`]: echeqEndorseData[`_${credentialType}`],
        userId,
        ...endorseDataObject,
        ...echeqActionCleaned,
        beneficiary: beneficiary.document,
        beneficiaryName: beneficiary.reference,
    };
    try {
        const response = yield call(echecksMiddleware.sendMultipleEndorseEcheq, params);

        if (response.type === "W" && response.data.code !== "API516W") {
            if (response.data.code === "COR020W") {
                yield put(
                    notificationActions.showNotification(response.data?.message, "error", ["echecks/endorse/confirm"]),
                );
                if (response.data?.data?.password) {
                    const msj =
                        credentialType === "password"
                            ? "preferences.password.password.incorrect"
                            : "login.step1.secondFactor.invalid";
                    yield put(
                        notificationActions.showNotification(i18n.get(msj), "error", ["echecks/endorse/confirm"]),
                    );
                    formikBag.setErrors({
                        credential: i18n.get(msj),
                    });
                }
            } else {
                notificationActions.showNotification(response.data.message, "error", ["echecks/endorse/confirm"]);
            }
        } else {
            yield put(
                notificationActions.showNotification(
                    i18n.get("multiple.signatures.notification.processing"),
                    "warning",
                    ["multipleSignaturesProcessing"],
                ),
            );

            yield put(routerActions.replace("/echecks/multiple/processing"));
        }
    } finally {
        formikBag.setSubmitting(false);
    }
}

function* executeEcheqActionSagas({ echeqAction }) {
    const { userId } = sessionSelectors.getUser(store.getState());
    const params = { ...echeqAction, userId };
    const response = yield call(echecksMiddleware.executeEcheqAction, params);

    if (response.type === "W") {
        yield put(notificationActions.showNotification(response.data.message, "error", ["echecks"]));
    } else {
        const { description, error } = response.data.data;

        yield put({
            type: types.CLEAN_ECHEQ_ACTION,
        });
        yield put(routerActions.goBack());
        const depositMaxHour = configUtils.getInteger("echeq.deposit.warning.hour", 17);
        const actualHour = moment().hour();
        if (echeqAction.action === "DEPOSIT" && error === "success" && actualHour > depositMaxHour) {
            yield put(
                notificationActions.showNotification(i18n.get("echecks.deposit.warning.message"), "warning", [
                    "echecksMainPage",
                ]),
            );
        } else {
            yield put(notificationActions.showNotification(description, error, ["echecksMainPage"]));
        }
    }
}

function* executeEcheqTransactionActionSagas({ echeqAction }) {
    const { userId } = sessionSelectors.getUser(store.getState());
    const amount = calculateTotalAmount(echeqAction.echeqList);
    const params = { ...echeqAction, userId, amount };

    try {
        yield put(echeckActions.setEcheqActionSubmitting(true));

        const response = yield call(echecksMiddleware.executeEcheqTransactionAction, params);
        if (response.type === "W" && response.data.code !== "API516W") {
            if (response.data.code === "COR020W") {
                yield put(
                    notificationActions.showNotification(response.data?.message, "error", [
                        "/echecks/action/transaction/confirm",
                    ]),
                );
            } else {
                yield put(
                    notificationActions.showNotification(response.data.message, "error", [
                        "/echecks/action/transaction/confirm",
                    ]),
                );
            }
        } else {
            if (response.data.code === "API516W") {
                yield put(
                    notificationActions.showNotification(response.data?.data?.description, "error", [
                        "/echecks/action/transaction/confirm",
                    ]),
                );
                return;
            }

            yield put({
                type: types.CLEAN_ECHEQ_ACTION,
            });

            yield put({
                type: types.ECHECK_TICKET_SUCCESS,
                echeckTicket: {
                    status: ticketStates[response.data.code],
                    ...response.data,
                },
            });
        }
    } finally {
        yield put(echeckActions.setEcheqActionSubmitting(false));
    }
}

function* executeEcheqMultipleActionSagas({ echeqData, formikBag, credentialType }) {
    const { userId } = sessionSelectors.getUser(store.getState());
    const { echeqAction } = echeqData;
    const amount = calculateTotalAmount(echeqAction.echeqList);
    const params = {
        [`_${credentialType}`]: echeqData[`_${credentialType}`],
        userId,
        ...echeqAction,
        amount,
    };

    try {
        const response = yield call(echecksMiddleware.executeEcheqMultipleAction, params);

        if (response.type === "W" && response.data.code !== "API516W") {
            if (response.data.code === "COR020W") {
                yield put(
                    notificationActions.showNotification(response.data?.message, "error", ["echecks/cesion/confirm"]),
                );
                if (response.data?.data?.password) {
                    const msj =
                        credentialType === "password"
                            ? "preferences.password.password.incorrect"
                            : "login.step1.secondFactor.invalid";
                    yield put(notificationActions.showNotification(i18n.get(msj), "error", ["echecks/cesion/confirm"]));
                    formikBag.setErrors({
                        credential: i18n.get(msj),
                    });
                }
            } else {
                notificationActions.showNotification(response.data.message, "error", ["echecks/cesion/confirm"]);
            }
        } else {
            if (response.data.code === "API516W") {
                yield put(
                    notificationActions.showNotification(response.data?.data?.description, "error", [
                        "echecks/cesion/confirm",
                    ]),
                );
                return;
            }

            yield put({
                type: types.CLEAN_ECHEQ_ACTION,
            });

            yield put({
                type: types.ECHECK_TICKET_SUCCESS,
                echeckTicket: {
                    status: ticketStates[response.data.code],
                    ...response.data,
                },
            });
        }
    } finally {
        formikBag.setSubmitting(false);
    }
}

function* echeqDetailRequest({ echeqId }) {
    const response = yield call(echecksMiddleware.echeqDetailRequestMiddleware, echeqId);

    if (response.type === "W") {
        yield put(notificationActions.showNotification(response.data.message, "error", ["echecks"]));
    } else if (response.data.data.codigo !== "2400") {
        yield put(notificationActions.showNotification(response.data.data.description, "error", ["echecks"]));
    } else {
        yield put({
            type: types.ECHEQ_DETAIL_SUCCESS,
            echeqDetail: response.data.data.echeq,
            environmentCuit: response.data.data.environmentCuit,
        });
    }
}

function* amountEchecksRequest({ idAccount }) {
    const { userId } = sessionSelectors.getUser(store.getState());
    const params = { userId, idAccount };
    try {
        const response = yield call(echecksMiddleware.amountEchecks, params);
        if (response.type === "W") {
            yield put(notificationActions.showNotification(response.data.message, "error", ["echecks/emit"]));
        } else {
            yield put({
                type: types.AMOUNT_ECHECK_SUCCESS,
                echeckAmountData: response.data.data,
            });
        }
    } finally {
        yield put({
            type: types.SET_ECHEQ_FETCHING,
        });
    }
}

function* acceptReturnEcheq({ echeqAceptReturnData, formikBag, credentialType }) {
    const environmentCuit = echeckSelectors.getEnvironmentCuit(store.getState());
    const { echeqAction } = echeqAceptReturnData;
    const echeqActionCleaned = cleanEcheqObject(echeqAction, environmentCuit);
    const { userId } = sessionSelectors.getUser(store.getState());
    const params = {
        [`_${credentialType}`]: echeqAceptReturnData[`_${credentialType}`],
        ...echeqActionCleaned,
        userId,
    };

    try {
        const response = yield call(echecksMiddleware.acceptRequestReturnEcheq, params);
        const { description, error } = response.data.data;

        if (response.type === "W") {
            if (response.data.code === "COR020W") {
                yield put(notificationActions.showNotification(response.data?.message, "error", ["echecks/return"]));
                if (response.data?.data?.password) {
                    const msj =
                        credentialType === "password"
                            ? "preferences.password.password.incorrect"
                            : "login.step1.secondFactor.invalid";
                    yield put(notificationActions.showNotification(i18n.get(msj), "error", ["echecks/return"]));
                    formikBag.setErrors({
                        credential: i18n.get(msj),
                    });
                }
                // formikBag.setErrors(response.data.data);
            } else {
                yield put(notificationActions.showNotification(response.data.message, "error", ["echecks/return"]));
            }
        } else {
            // yield put(formActions.readTransaction({ pathname: `/transaction/${response.data.idTransaction}` }));

            // yield take("READ_TRANSACTION_REQUEST"); // waiting until finish action
            yield put({
                type: types.CLEAN_ECHEQ_ACTION,
            });

            yield put({
                type: types.ECHECK_TICKET_SUCCESS,
                echeckTicket: {
                    status: ticketStates[response.data.code],
                    ...response.data,
                },
            });
            if (ticketStates[response.data.code] === ticketStates.COR000I) {
                yield put(notificationActions.showNotification(description, error, ["echecks/return"]));
            }
        }
    } finally {
        formikBag.setSubmitting(false);
    }
}

function* getBankedBenefiicary({ document, setSubmitting, stringType, setOpenSidebarForm, resetForm = null }) {
    try {
        const response = yield call(echecksMiddleware.bankedBeneficiary, document);
        if (response.type === "W") {
            yield put(
                notificationActions.showNotification(response.data.message, "error", [
                    "echecks/beneficary",
                    "cedip/transmit",
                ]),
            );
        } else {
            const { beneficiary, codigo, description } = response.data.data;

            if (codigo !== "2500") {
                yield put(
                    notificationActions.showNotification(description, "error", [
                        "echecks/beneficary",
                        "cedip/transmit",
                    ]),
                );
            } else {
                const beneficiaryInfo = {
                    document: beneficiary.beneficiario_documento_tipo.concat(" ", beneficiary.beneficiario_documento),
                    reference: beneficiary.beneficiario_razon_social,
                };

                yield put(echeckActions.beneficiaryEcheq(beneficiaryInfo, stringType));
            }
        }
    } finally {
        setSubmitting(false);
        if (setOpenSidebarForm) {
            setOpenSidebarForm(false);
        }
        if (resetForm) {
            resetForm();
        }
    }
}

function* updateEcheckAmount({ requestEcheckAmountData, formikBag, credentialType }) {
    try {
        const response = yield call(echecksMiddleware.updateEcheckAmount, requestEcheckAmountData);

        if (response.type === "W") {
            if (response.data?.code === "COR020W") {
                yield put(
                    notificationActions.showNotification(response.data?.message, "error", ["TransferSummaryPage"]),
                );
                if (response?.data?.data?.otp) {
                    yield put(
                        notificationActions.showNotification(response.data.data.otp, "error", ["TransferSummaryPage"]),
                    );
                }
                if (response.data?.data?.password) {
                    const msj =
                        credentialType === "password"
                            ? "preferences.password.password.incorrect"
                            : "login.step1.secondFactor.invalid";
                    yield put(notificationActions.showNotification(i18n.get(msj), "error", ["TransferSummaryPage"]));
                    formikBag.setErrors({
                        credential: i18n.get(msj),
                    });
                }
            } else {
                yield put(
                    notificationActions.showNotification(response.data?.message, "error", ["TransferSummaryPage"]),
                );
            }
        } else {
            yield put({
                type: types.ECHECK_TICKET_SUCCESS,
                echeckTicket: {
                    status: ticketStates[response.data.code],
                    ...response.data,
                },
            });
            if (ticketStates[response.data.code] === ticketStates.COR000I) {
                yield put(
                    notificationActions.showNotification(response.data.message, "success", ["requestEcheqTransaction"]),
                );
            }
        }
    } finally {
        formikBag.setSubmitting(false);
    }
}

function* solicitAvalRequest({ avalToSolicit, formikBag, credentialType }) {
    try {
        const { userId } = sessionSelectors.getUser(store.getState());
        const params = {
            ...avalToSolicit,
            userId,
        };
        const response = yield call(echecksMiddleware.solicitAvalRequest, params);
        if (response.type === "W") {
            if (response.data?.code === "COR020W") {
                if (response.data?.data?.password) {
                    const msj =
                        credentialType === "password"
                            ? "preferences.password.password.incorrect"
                            : "login.step1.secondFactor.invalid";
                    yield put(notificationActions.showNotification(i18n.get(msj), "error", ["echecks/aval/confirm"]));
                    formikBag.setErrors({
                        credential: i18n.get(msj),
                    });
                } else {
                    yield put(routerActions.push("/echecks"));
                    yield put(notificationActions.showNotification(response.data?.data?.message, "error", ["echecks"]));
                }
            } else {
                yield put(routerActions.push("/echecks"));
                yield put(notificationActions.showNotification(response.data?.data?.message, "error", ["echecks"]));
            }
        } else {
            yield put({
                type: types.ECHECK_TICKET_SUCCESS,
                echeckTicket: {
                    status: ticketStates[response.data.code],
                    ...response.data,
                },
            });
            if (ticketStates[response.data.code] === ticketStates.COR000I) {
                yield put(
                    notificationActions.showNotification(response.data?.data?.message, "success", ["requestEcheqAval"]),
                );
            }
        }
    } finally {
        formikBag.setSubmitting(false);
    }
}

function* updateStateAvalRequest({ avalToUpdate, formikBag, credentialType }) {
    try {
        const status = yield select(echeckSelectors.getEcheqsToDoActions);
        const { userId } = sessionSelectors.getUser(store.getState());
        const params = {
            ...avalToUpdate,
            userId,
        };
        let response;
        if (avalToUpdate?.action === "AVAL-ANULATE" || status?.action === "AVAL-ANULATE") {
            response = yield call(echecksMiddleware.anulateAvalRequest, params);
        } else if (avalToUpdate?.action === "AVAL-ADMIT" || status?.action === "AVAL-ADMIT") {
            response = yield call(echecksMiddleware.admitAvalRequest, params);
        } else if (avalToUpdate?.action === "AVAL-REPUDIATE" || status?.action === "AVAL-REPUDIATE") {
            response = yield call(echecksMiddleware.repudiateAvalRequest, params);
        }

        if (response?.type === "W") {
            if (response.data?.code === "COR020W") {
                if (response.data?.data?.password) {
                    const msj =
                        credentialType === "password"
                            ? "preferences.password.password.incorrect"
                            : "login.step1.secondFactor.invalid";
                    yield put(notificationActions.showNotification(i18n.get(msj), "error", ["echecks/aval/confirm"]));
                    formikBag.setErrors({
                        credential: i18n.get(msj),
                    });
                } else {
                    yield put(routerActions.push("/echecks"));
                    yield put(notificationActions.showNotification(response.data?.data?.message, "error", ["echecks"]));
                }
            } else {
                yield put(routerActions.push("/echecks"));
                yield put(notificationActions.showNotification(response.data?.data?.message, "error", ["echecks"]));
            }
        } else {
            yield put({
                type: types.ECHECK_TICKET_SUCCESS,
                echeckTicket: {
                    status: ticketStates[response.data.code],
                    ...response?.data,
                },
            });
            if (ticketStates[response?.data?.code] === ticketStates.COR000I) {
                yield put(
                    notificationActions.showNotification(response?.data?.data?.message, "success", ["updateEcheqAval"]),
                );
            }
        }
    } finally {
        formikBag.setSubmitting(false);
    }
}

function* validateMassiveEcheq({ summary, formikBag, credentialType }) {
    const params = { ...summary, idForm: "LoadEcheqMassive" };
    try {
        const response = yield call(echecksMiddleware.validateMassiveEcheq, params);

        if (response.type === "W") {
            if (response.data.code === "API570W") {
                yield put(
                    notificationActions.showNotification(response.data?.data?.message, "error", [
                        "massiveEcheckSummary",
                    ]),
                );
                return;
            }

            if (response.data.code === "COR020W") {
                yield put(
                    notificationActions.showNotification(response.data?.message, "error", ["massiveEcheckSummary"]),
                );
                if (response.data?.data?.password || response.data?.data?.otp) {
                    const msj =
                        credentialType === "password"
                            ? "preferences.password.password.incorrect"
                            : "login.step1.secondFactor.invalid";
                    yield put(notificationActions.showNotification(i18n.get(msj), "error", ["massiveEcheckSummary"]));
                    formikBag.setErrors({
                        credential: i18n.get(msj),
                    });
                }
                return;
            }

            yield put(
                notificationActions.showNotification(response.data?.data?.NO_FIELD || response.data?.message, "error", [
                    "massiveEcheckSummary",
                ]),
            );
        } else {
            yield put({
                type: types.ECHECK_TICKET_SUCCESS,
                echeckTicket: {
                    status: ticketStates[response.data.code],
                    ...response.data,
                },
            });
        }
    } finally {
        formikBag.setSubmitting(false);
    }
}

function* cancelTransfer({ summary, formikBag, credentialType }) {
    const params = {
        [`_${credentialType}`]: summary[`_${credentialType}`],
    };

    const idTransaction =
        summary.idTransaction ?? summary.cesionData?.cesionData?.idTransaction ?? summary.echeqAction?.idTransaction;
    const action = summary.action ?? summary.cesionData?.cesionData?.action ?? summary.echeqAction?.action;

    // Determinar la ruta de notificación según el tipo de acción
    const isRepudiateAction = repudiateActions.includes(action);
    const notificationScope = isRepudiateAction ? "echecks/action/transaction/confirm" : "echecks/cesion/confirm";

    try {
        const response = yield call(form.cancelTransaction, idTransaction, { ...params });
        const { hasValidationError } = getStateByCode(response.data.code);

        if (response.type === "W") {
            if (hasValidationError) {
                const msj = response.data?.data[credentialType];
                if (msj) {
                    yield put(notificationActions.showNotification(msj, "error", [notificationScope]));
                    formikBag.setErrors({
                        credential: msj,
                    });
                    return;
                }
                yield put(notificationActions.showNotification(response.data?.message, "error", [notificationScope]));
            }
        } else {
            yield call(
                form.updateUserEditor,
                summary.idTransaction,
                summary.userId,
                "CANCELLED",
                null,
                summary.cancelTransaction,
            );

            if (response.data.code === "COR000I") {
                yield put({
                    type: types.ECHECK_TICKET_SUCCESS,
                    echeckTicket: {
                        status: "CANCELLED",
                        ...response.data,
                    },
                });

                yield put({
                    type: types.SET_ECHEQ_TODO_ACTIONS,
                    echeqs: {},
                });
            } else {
                yield put({
                    type: types.ECHECK_TICKET_SUCCESS,
                    echeckTicket: {
                        status: ticketStates[response.data.code],
                        ...response.data,
                        idTransactionToCancel: idTransaction,
                    },
                });
            }
        }
    } finally {
        formikBag.setSubmitting(false);
    }
}

function* cancelEndorseEcheq({ echeqEndorseData, formikBag, credentialType }) {
    const { userId } = sessionSelectors.getUser(store.getState());
    const { endorseData, echeqAction } = echeqEndorseData;
    const { endorseData: endorseDataObject, beneficiary } = endorseData;
    const echeqActionCleaned = cleanEcheqObject(echeqAction);
    const amount = calculateTotalAmount(echeqAction.echeqList);
    const params = {
        [`_${credentialType}`]: echeqEndorseData[`_${credentialType}`],
        userId,
        ...endorseDataObject,
        ...echeqAction,
    };

    try {
        const response = yield call(echecksMiddleware.cancelEndorseEcheq, params);
        const { description, error } = response.data.data;

        if (response.type === "W" && response.data.code !== "API516W") {
            if (response.data.code === "COR020W") {
                if (response.data.data.otp) {
                    formikBag.setErrors({
                        credential: i18n.get("validate.secondFactor.invalid"),
                    });
                    formikBag.setSubmitting(false);
                    return;
                }
            } else {
                yield put(
                    notificationActions.showNotification(response.data.message, "error", ["echecks/endorse/confirm"]),
                );
            }
        } else {
            if (response.data.code === "API516W") {
                yield put(push(`/echecks/action/cancelendorse/failed/${response.data.idTransaction}`));
                yield put(
                    notificationActions.showNotification(response.data?.data?.description, "error", [
                        "echecks/endorse/confirm",
                    ]),
                );
                return;
            }
            // yield put(formActions.readTransaction({ pathname: `/transaction/${response.data.idTransaction}` }));
            // yield take("READ_TRANSACTION_REQUEST"); // waiting until finish action
            yield put({
                type: types.CLEAN_ECHEQ_ACTION,
            });

            yield put({
                type: types.ECHECK_TICKET_SUCCESS,
                echeckTicket: {
                    status: ticketStates[response.data.code],
                    ...response.data,
                },
            });

            yield put(push(`/echecks/action/cancelendorse/success/${response.data.idTransaction}`));

            if (ticketStates[response.data.code] === ticketStates.COR000I) {
                yield put(notificationActions.showNotification(description, error, ["echecks/endorse"]));
            }
        }
    } finally {
        formikBag.setSubmitting(false);
    }
}

function* requestCac({ cacData, formikBag, credentialType }) {
    try {
        const { userId } = sessionSelectors.getUser(store.getState());
        const params = {
            ...cacData,
            userId,
        };
        const response = yield call(echecksMiddleware.requestCac, params);
        if (response.type === "W") {
            if (response.data?.code === "COR020W") {
                if (response.data?.data?.password || response.data?.data?.otp) {
                    const msj =
                        credentialType === "password"
                            ? "preferences.password.password.incorrect"
                            : "login.step1.secondFactor.invalid";
                    yield put(notificationActions.showNotification(i18n.get(msj), "error", ["RequestCacSummaryPage"]));
                    formikBag.setErrors({
                        credential: i18n.get(msj),
                    });
                } else {
                    yield put(routerActions.push("/echecks"));
                    yield put(notificationActions.showNotification(response.data?.data?.message, "error", ["echecks"]));
                }
            } else {
                yield put(routerActions.push("/echecks"));
                yield put(notificationActions.showNotification(response.data?.data?.message, "error", ["echecks"]));
            }
        } else {
            yield put({
                type: types.ECHECK_TICKET_SUCCESS,
                echeckTicket: {
                    status: ticketStates[response.data.code],
                    ...response.data,
                },
            });
            if (ticketStates[response.data.code] === ticketStates.COR000I) {
                yield put(
                    notificationActions.showNotification(response.data?.data?.message, "success", ["requestEcheqAval"]),
                );
            }
        }
    } finally {
        formikBag.setSubmitting(false);
    }
}

function* acceptReturnRejected({ data, formikBag, credentialType }) {
    try {
        console.log(data);
        const response = yield call(echecksMiddleware.acceptReturnRejected, data);
        if (response.type === "W") {
            if (response.data?.code === "BAK631W") {
                yield put({
                    type: types.ECHECK_TICKET_SUCCESS,
                    echeckTicket: {
                        status: ticketStates[response.data.code],
                        ...response.data,
                    },
                });

                return;
            }

            if (response.data?.code === "COR020W") {
                if (response.data?.data?.password || response.data?.data?.otp) {
                    const msj =
                        credentialType === "password"
                            ? "preferences.password.password.incorrect"
                            : "login.step1.secondFactor.invalid";
                    yield put(notificationActions.showNotification(i18n.get(msj), "error", ["AcceptReturnRejected"]));
                    formikBag.setErrors({ credential: i18n.get(msj) });
                    return;
                }
            }
            const message = credentialsErrorMessage(response.data?.data) || response.data?.message;
            yield put(notificationActions.showNotification(message, "error", ["AcceptReturnRejected"]));
        }

        if (response.type === "I") {
            yield put({
                type: types.ECHECK_TICKET_SUCCESS,
                echeckTicket: {
                    status: ticketStates[response.data.code],
                    ...response.data,
                },
            });
            if (ticketStates[response.data.code] === ticketStates.COR000I) {
                yield put(
                    notificationActions.showNotification(response.data?.data?.message, "success", [
                        "AcceptReturnRejected",
                    ]),
                );
            }
        }
    } finally {
        formikBag.setSubmitting(false);
    }
}
